import React, { useState, useEffect, useRef } from 'react';
import { useLocation} from 'react-router-dom';
import Button from '../../components/elements/button';
import { Upload, SendWhite, Download } from '../../assets/icons';
import '../../assets/css/pages/case.css';
import { UploadModal } from '../../components/case/upload';
import { AppointmentModal } from '../../components/case/appointment';
import { messageApi } from '../../network/apis/message';

const CasePage = () => {
    const [showAppointmentForm, setShowAppointmentForm] = useState(false);
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [showDetailsOnMobile, setShowDetailsOnMobile] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const messagesEndRef = useRef(null);
    const location = useLocation();
    
    // Track if AI assistant is typing
    const [isAiTyping, setIsAiTyping] = useState(false);
    const [, setIsLoadingMessages] = useState(false);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    
    const [messages, setMessages] = useState([]);

    // Auto-scroll to bottom of messages
    useEffect(() => {
        fetchMessages();
        scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const fetchMessages = async () => {
        // Fetch messages from API or local storage
        setIsLoadingMessages(true);
        try {
            const response = await messageApi.getMessages({"chat_id": "case_"+caseData.case_code});
            if (response.data.data && response.data.data !== null) {
                setMessages(response.data.data);
            } else {
                setMessages([]);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setIsLoadingMessages(false);
        }
    };

    const handleSendMessage = async () => {
        if (newMessage.trim() && !isSendingMessage) {
            setIsSendingMessage(true);
            // const message = {
            //     sender: 'patient',
            //     senderName: 'You',
            //     time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
            //     content: newMessage,
            //     attachments: []
            // };

            try {
                // Send message to API
                const lowerMessage = newMessage.toLowerCase();
                if (lowerMessage.includes('@ai') || lowerMessage.includes('@aquabot')) {
                    handleAiResponse(newMessage);
                }
                const message = await messageApi.newMessage({
                    chat_id: "case_"+caseData.case_code,
                    content: newMessage,
                    attachments: []
                });
                console.log(message.data.data);
                setMessages([...messages, message.data.data]);
                setNewMessage('');
                
                // Check for AI trigger words
            }
            catch (error) { 
                console.error('Error sending message:', error);
            } finally {
                setIsSendingMessage(false);
            }

        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleAiResponse = (userMessage) => {
        // Show typing indicator
        setIsAiTyping(true);
        
        // Simulate AI processing time
        setTimeout(() => {
            let response;
            const lowerMessage = userMessage.toLowerCase();
            
            if (lowerMessage.includes('water quality')) {
                response = "Based on your water parameters, I'd recommend testing ammonia and nitrite levels. The current pH of 7.2 is within acceptable range for most fish species, but the elevated mortality rate might indicate water quality issues.";
            } else if (lowerMessage.includes('treatment') || lowerMessage.includes('medicine')) {
                response = "Before recommending treatments, Dr. Johnson would need more information. Could you share more details about symptoms? In the meantime, ensure good water circulation and consider reducing feeding until diagnosis is confirmed.";
            } else if (lowerMessage.includes('visit') || lowerMessage.includes('appointment')) {
                response = "I've notified Dr. Johnson about your request for a farm visit. You can schedule one using the 'Request Farm Visit' button below.";
            } else {
                response = "I've analyzed your case details. The combination of erratic swimming and loss of appetite could indicate several issues like parasitic infection or water quality problems. Dr. Johnson will be able to provide a more specific diagnosis soon.";
            }
            
            const aiMessage = {
                sender: 'bot',
                senderName: 'AquaBot',
                time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
                content: response,
                attachments: []
            };
            
            setMessages(prev => [...prev, aiMessage]);
            setIsAiTyping(false);
        }, 2000);
    };

    const handleFileUpload = (files) => {
        const message = {
            sender: 'patient',
            senderName: 'You',
            time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
            content: 'File uploaded',
            attachments: files
        };

        setMessages([...messages, message]);
        setShowUploadForm(false);
    };

    // Mock case data
    // const caseData = {
    //     id: 'CS001',
    //     title: 'Abnormal Fish Behavior',
    //     status: 'in-progress',
    //     priority: 'HIGH',
    //     createdAt: '2024-03-15',
    //     description: 'Fish showing erratic swimming patterns and loss of appetite',
    //     symptoms: ['Erratic swimming', 'Loss of appetite', 'Discoloration'],
    //     pondDetails: [
    //         {
    //             population: '1000',
    //             mortality: '5.2',
    //             fishAge: '12',
    //             fishType: 'tilapia',
    //             pondType: 'earthen',
    //             waterSource: 'river',
    //             previousTreatment: 'Applied salt treatment at 2kg/1000L two weeks ago',
    //             temperature: '28',
    //             pH: '7.2',
    //             oxygen: '6.5',
    //             ammonia: '0.02',
    //             isExpanded: true // Start expanded
    //         },
    //         {
    //             population: '800',
    //             mortality: '3.8',
    //             fishAge: '8',
    //             fishType: 'catfish',
    //             pondType: 'concrete',
    //             waterSource: 'well',
    //             previousTreatment: '',
    //             temperature: '26',
    //             pH: '6.8',
    //             oxygen: '5.9',
    //             ammonia: '0.04',
    //             isExpanded: false // Start collapsed
    //         }
    //     ],
    //     doctor: {
    //         id: 'D001',
    //         name: 'Dr. Sarah Johnson',
    //         specialty: 'Fish Health Specialist',
    //         image: '/doctors/sarah.jpg'
    //     },
    //     lab: {
    //         id: 'L001',
    //         name: 'Aqua Analytics Lab',
    //         location: 'Lagos, Nigeria'
    //     },
    //     timeline: [
    //         {
    //             date: '2024-03-15',
    //             type: 'case-created',
    //             content: 'Case reported'
    //         },
    //         {
    //             date: '2024-03-16',
    //             type: 'doctor-assigned',
    //             content: 'Dr. Sarah Johnson assigned to case'
    //         }
    //         // More timeline events...
    //     ]
    // };
    const caseData = location.state?.case
    

    // const [messages, setMessages] = useState([
    //     {
    //         sender: 'doctor',
    //         senderName: 'Dr. Sarah Johnson',
    //         time: '10:30 AM',
    //         content: 'Hello! How can I help you today?',
    //         attachments: []
    //     },
    //     {
    //         sender: 'patient',
    //         senderName: 'You',
    //         time: '10:35 AM',
    //         content: 'My fish are showing some unusual behavior',
    //         attachments: []
    //     }
    //     // More messages...
    // ]);

    // const [newMessage, setNewMessage] = useState('');

    // const handleSendMessage = () => {
    //     if (newMessage) {
    //         const message = {
    //             sender: 'patient',
    //             senderName: 'You',
    //             time: new Date().toLocaleTimeString(),
    //             content: newMessage,
    //             attachments: []
    //         };

    //         setMessages([...messages, message]);
    //         setNewMessage('');
    //     }
    // };

    // const handleFileUpload = (files) => {
    //     const message = {
    //         sender: 'patient',
    //         senderName: 'You',
    //         time: new Date().toLocaleTimeString(),
    //         content: 'File uploaded',
    //         attachments: files
    //     };

    //     setMessages([...messages, message]);
    //     setShowUploadForm(false);
    // };

    const handleBookAppointment = (appointment) => {
        console.log(appointment);
        setShowAppointmentForm(false);
    };

    // Add these helper functions above your component
    const formatFishType = (fishType) => {
        if (!fishType) return null;
        
        const fishTypes = {
            'tilapia': 'Tilapia',
            'catfish': 'Catfish',
            'carp': 'Carp',
            'trout': 'Trout',
            'salmon': 'Salmon',
            'other': 'Other'
        };
        
        return fishTypes[fishType] || fishType;
    };

    const formatPondType = (pondType) => {
        if (!pondType) return null;
        
        const pondTypes = {
            'earthen': 'Earthen Pond',
            'concrete': 'Concrete Tank',
            'cage': 'Cage Culture',
            'ras': 'Recirculating Aquaculture System (RAS)',
            'biofloc': 'Biofloc System',
            'other': 'Other'
        };
        
        return pondTypes[pondType] || pondType;
    };

    const formatWaterSource = (waterSource) => {
        if (!waterSource) return null;
        
        const waterSources = {
            'river': 'River/Stream',
            'well': 'Borehole/Well',
            'rain': 'Rainwater',
            'municipal': 'Municipal Supply',
            'spring': 'Spring',
            'other': 'Other'
        };
        
        return waterSources[waterSource] || waterSource;
    };
    

    return (
        <div className="case-page">
            {/* <div className="case-header"> */}
                
                
                {/* <div className="case-actions">
                    <Button
                        // icon={<img src={Message} />}
                        text="Messages"
                        onClick={() => setShowMessageForm(true)}
                    />
                    <Button
                        // icon={<img src={Calendar} />}
                        text="Request Farm Visit"
                        variant="primary"
                        onClick={() => setShowAppointmentForm(true)}
                    />
                </div> */}
            {/* </div> */}

            <div className={`case-layout ${showDetailsOnMobile ? 'show-details-mobile' : ''}`}>
            <div>
            <div className="case-title">
                    <h1>{caseData.title}</h1>
                {/* <div className="mobile-actions"> */}
                    <Button
                        // icon={<img src={Info} alt="Details" />}
                        text="View Details"
                        onClick={() => setShowDetailsOnMobile(!showDetailsOnMobile)}
                        className="mobile-only"
                    />
                {/* </div> */}
                </div>
                <div className="case-details-column">
                    <div className="mobile-close">
                        <Button
                            text="Back to Chat"
                            variant="text"
                            onClick={() => setShowDetailsOnMobile(false)}
                            className="mobile-only"
                        />
                    </div>
                    <div className="case-info">
                        <div className="status-card">
                            <h3>Case Status</h3>
                            <span className={`status ${caseData.status.toLowerCase()}`}>
                                {caseData.status}
                            </span>
                            <p>Reported on: {caseData.created_at}</p>
                        </div>

                        <div className="details-card">
                            <h3>Case Details</h3>
                            <p>{caseData.description}</p>
                            
                            <div className="symptoms-list">
                                <h4>Reported Symptoms</h4>
                                <ul>
                                    {caseData.symptoms.map((symptom, index) => (
                                        <li key={index}>{symptom}</li>
                                    ))}
                                </ul>
                            </div>

                            <div className="pond-details-section">
                                <h4>Affected Ponds</h4>
                                
                                {caseData.pondDetails && caseData.pondDetails.length > 0 ? (
                                    <div className="ponds-accordion">
                                        {caseData.pondDetails.map((pond, pondIndex) => (
                                            <div key={pondIndex} className="pond-accordion-item">
                                                <div 
                                                    className="pond-accordion-header"
                                                    onClick={() => {
                                                        // Toggle pond expanded state
                                                        const updatedPonds = [...caseData.pondDetails];
                                                        updatedPonds[pondIndex] = {
                                                            ...updatedPonds[pondIndex],
                                                            isExpanded: !updatedPonds[pondIndex].isExpanded
                                                        };
                                                        // Update caseData with expanded state
                                                        // Note: In a real implementation, you might need to use state for this
                                                    }}
                                                >
                                                    <h5>Pond {pondIndex + 1}</h5>
                                                    <span className="accordion-icon">
                                                        {pond.isExpanded ? '▼' : '►'}
                                                    </span>
                                                </div>
                                                
                                                {(pond.isExpanded !== false) && (
                                                    <div className="pond-accordion-content">
                                                        <div className="pond-info-grid">
                                                            <div className="parameter-group">
                                                                <h6>Population Details</h6>
                                                                <div className="parameter-item">
                                                                    <label>Fish Population</label>
                                                                    <span>{pond.population || 'Not specified'}</span>
                                                                </div>
                                                                <div className="parameter-item">
                                                                    <label>Mortality Rate</label>
                                                                    <span>{pond.mortality ? `${pond.mortality}%` : 'Not specified'}</span>
                                                                </div>
                                                                <div className="parameter-item">
                                                                    <label>Fish Age</label>
                                                                    <span>{pond.fishAge ? `${pond.fishAge} weeks` : 'Not specified'}</span>
                                                                </div>
                                                                <div className="parameter-item">
                                                                    <label>Fish Type</label>
                                                                    <span>{formatFishType(pond.fishType) || 'Not specified'}</span>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="parameter-group">
                                                                <h6>Pond System</h6>
                                                                <div className="parameter-item">
                                                                    <label>System Type</label>
                                                                    <span>{formatPondType(pond.pondType) || 'Not specified'}</span>
                                                                </div>
                                                                <div className="parameter-item">
                                                                    <label>Water Source</label>
                                                                    <span>{formatWaterSource(pond.waterSource) || 'Not specified'}</span>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="parameter-group">
                                                                <h6>Water Parameters</h6>
                                                                <div className="parameter-item">
                                                                    <label>Temperature</label>
                                                                    <span>{pond.temperature ? `${pond.temperature}°C` : 'Not measured'}</span>
                                                                </div>
                                                                <div className="parameter-item">
                                                                    <label>pH</label>
                                                                    <span>{pond.pH || 'Not measured'}</span>
                                                                </div>
                                                                <div className="parameter-item">
                                                                    <label>Dissolved Oxygen</label>
                                                                    <span>{pond.oxygen ? `${pond.oxygen} mg/L` : 'Not measured'}</span>
                                                                </div>
                                                                <div className="parameter-item">
                                                                    <label>Ammonia</label>
                                                                    <span>{pond.ammonia ? `${pond.ammonia} mg/L` : 'Not measured'}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        {pond.previousTreatment && (
                                                            <div className="previous-treatment">
                                                                <h6>Previous Treatments</h6>
                                                                <p>{pond.previousTreatment}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-pond-details">
                                        <p>No pond details available</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="assigned-parties">
                            <div className="doctor-card">
                                <img src={caseData.doctor?.image} alt={caseData.doctor?.name} />
                                <div className="doctor-info">
                                    <h4>{caseData.doctor?.name}</h4>
                                    <p>{caseData.doctor?.specialty}</p>
                                </div>
                            </div>

                            <div className="lab-card">
                                <h4>{caseData.lab?.name}</h4>
                                <p>{caseData.lab?.location}</p>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

                <div className="case-chat-column">
                    <div className="chat-container">
                        <div className="chat-header">
                            <h3>Chat</h3>
                            {/* <div className="chat-quick-actions">
                                <Button
                                    text="Request Visit"
                                    variant="outline"
                                    icon={<img src={Calendar} alt="Visit" />}
                                    onClick={() => setShowAppointmentForm(true)}
                                />
                                <Button
                                    text="Ask AI"
                                    variant="outline"
                                    icon={<img src={Robot} alt="Ask AI" />}
                                    onClick={() => {
                                        setNewMessage(prev => prev + " @AquaBot ");
                                    }}
                                />
                            </div> */}
                        </div>
                        
                        <div className="chat-messages">
                            {messages.map((message, index) => (
                                <div key={index} className={`message-wrapper ${message.sender_role.toLowerCase()}`}>
                                    <div className="message">
                                        {message.sender_role.toLowerCase() !== 'farmer' && (
                                            <div className="sender-name">{message.sender_name}</div>
                                        )}
                                        <div>
                                        <div className="message-content" style={{ whiteSpace: 'pre-wrap' }}>
                                            {message.content}
                                            {message.attachments && message.attachments.length > 0 && (
                                                <div className="message-attachments">
                                                    {message.attachments.map((file, i) => (
                                                        <div key={i} className="attachment">
                                                            <span>{file.name}</span>
                                                            <Button icon={<img src={Download} alt="Download" />} variant="text" />
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="message-time">{message.created_at}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            
                            {isAiTyping && (
                                <div className="message-wrapper bot">
                                    <div className="message">
                                        <div className="sender-name">AquaBot</div>
                                        <div className="message-content typing-indicator">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            <div ref={messagesEndRef} />
                        </div>

                        

                        <div className="chat-input">
                            <textarea 
                                placeholder="Type your message... (Use @AquaBot to get AI assistance)"
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <div className="input-actions">
                            {/* <Button 
                                    text="Request farm visit" 
                                    variant="chip" 
                                    onClick={() => setNewMessage("@AquaBot I'd like to request a farm visit")}
                                /> */}
                                <Button variant='transparent' icon={<img src={Upload} alt="Upload" />} onClick={() => setShowUploadForm(true)} />
                                <Button 
                                    icon={<img src={SendWhite} alt="Send" />} 
                                    onClick={handleSendMessage} 
                                    loading={isSendingMessage}
                                    disabled={isSendingMessage || !newMessage.trim()}
                                />
                            </div>
                        </div>

                        {/* <div className="chat-suggestions">
                            <div className="suggestion-chips"> */}
                                {/* <Button 
                                    text="Water quality issues" 
                                    variant="chip" 
                                    onClick={() => setNewMessage("@AquaBot I'm concerned about water quality issues")}
                                /> */}
                                {/* <Button 
                                    text="Treatment options" 
                                    variant="chip" 
                                    onClick={() => setNewMessage("@AquaBot What treatment options do you suggest?")}
                                /> */}
                                
                            {/* </div>
                        </div> */}
                    </div>
                </div>
            </div>

            {/* Modals with overlay */}
            {showAppointmentForm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <AppointmentModal 
                            doctor={caseData.doctor}
                            onSubmit={handleBookAppointment}
                            onClose={() => setShowAppointmentForm(false)}
                        />
                    </div>
                </div>
            )}

            {showUploadForm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <UploadModal
                            onUpload={handleFileUpload}
                            onClose={() => setShowUploadForm(false)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CasePage;